import React, { useState, useEffect } from 'react';
import {
  Send,
  Users,
  BarChart3,
  Plus,
  Search,
  Filter,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ShieldAlert,
  Eye,
  RefreshCw,
  Mail,
  UserCheck,
  Ban,
  FileText,
  TrendingUp,
} from 'lucide-react';
import Swal from 'sweetalert2';
import CampaignWizardModal from '../components/CampaignWizardModal';
import CampaignDetailModal from '../components/CampaignDetailModal';
import SubscriberDetailModal from '../components/SubscriberDetailModal';
import {
  getCampaigns,
  getSubscribers,
  getNewsletterStats,
} from '../../../services/newsNewsletterApi';

export default function NewsletterTab() {
  const [subTab, setSubTab] = useState('campaigns'); // 'campaigns' | 'subscribers' | 'stats'

  // Campaigns State
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [campaignsPagination, setCampaignsPagination] = useState({ total: 0, pages: 1 });
  const [selectedCampaignStatus, setSelectedCampaignStatus] = useState('');

  // Subscribers State
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [subscribersSearch, setSubscribersSearch] = useState('');
  const [subscribersStatus, setSubscribersStatus] = useState('');
  const [subscribersLocale, setSubscribersLocale] = useState('');
  const [subscribersExcludedOnly, setSubscribersExcludedOnly] = useState(false);
  const [subscribersPage, setSubscribersPage] = useState(1);
  const [subscribersPagination, setSubscribersPagination] = useState({ total: 0, pages: 1 });
  const [subscribersSummary, setSubscribersSummary] = useState({
    total: 0,
    activeCount: 0,
    unsubscribedCount: 0,
    excludedCount: 0,
  });

  // Global Stats State
  const [globalStats, setGlobalStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Modals
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [detailSubscriber, setDetailSubscriber] = useState(null);

  useEffect(() => {
    if (subTab === 'campaigns') {
      loadCampaigns();
    } else if (subTab === 'subscribers') {
      loadSubscribers();
    } else if (subTab === 'stats') {
      loadGlobalStats();
    }
  }, [
    subTab,
    selectedCampaignStatus,
    campaignsPage,
    subscribersSearch,
    subscribersStatus,
    subscribersLocale,
    subscribersExcludedOnly,
    subscribersPage,
  ]);

  const loadCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const params = {
        page: campaignsPage,
        limit: 10,
      };
      if (selectedCampaignStatus) params.status = selectedCampaignStatus;

      const res = await getCampaigns(params);
      if (res?.success) {
        setCampaigns(res.data || []);
        setCampaignsPagination(res.pagination || { total: res.data?.length || 0, pages: 1 });
      }
    } catch (err) {
      console.error('Erreur chargement campagnes:', err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const loadSubscribers = async () => {
    setSubscribersLoading(true);
    try {
      const params = {
        page: subscribersPage,
        limit: 12,
      };
      if (subscribersSearch.trim()) params.search = subscribersSearch.trim();
      if (subscribersStatus) params.status = subscribersStatus;
      if (subscribersLocale) params.preferredLocale = subscribersLocale;
      if (subscribersExcludedOnly) params.isExcluded = true;

      const res = await getSubscribers(params);
      if (res?.success) {
        setSubscribers(res.data || []);
        setSubscribersPagination(res.pagination || { total: res.data?.length || 0, pages: 1 });
        if (res.summary) {
          setSubscribersSummary(res.summary);
        }
      }
    } catch (err) {
      console.error('Erreur chargement abonnés:', err);
    } finally {
      setSubscribersLoading(false);
    }
  };

  const loadGlobalStats = async () => {
    setStatsLoading(true);
    try {
      const res = await getNewsletterStats();
      if (res?.success) {
        setGlobalStats(res.data || {});
      }
    } catch (err) {
      console.error('Erreur chargement statistiques:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getCampaignBadge = (status) => {
    const map = {
      DRAFT: { label: 'Brouillon', class: 'neutral' },
      SCHEDULED: { label: 'Planifié', class: 'info' },
      SENDING: { label: 'En cours d’envoi', class: 'warning' },
      SENT: { label: 'Envoyé avec succès', class: 'success' },
      COMPLETED_WITH_UNCERTAIN: { label: 'Terminé (Incertains à réconcilier)', class: 'warning' },
      CANCELLED: { label: 'Annulé', class: 'danger' },
    };
    const c = map[status] || { label: status, class: 'neutral' };
    return <span className={`an-badge an-badge-${c.class}`}>{c.label}</span>;
  };

  return (
    <div className="an-tab-container animate-fade-in">
      {/* Sub-Tabs Navigation */}
      <div className="an-subtabs-nav">
        <button
          type="button"
          className={`an-subtab-btn ${subTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setSubTab('campaigns')}
        >
          <Send size={16} />
          <span>Campagnes & Diffusions</span>
        </button>
        <button
          type="button"
          className={`an-subtab-btn ${subTab === 'subscribers' ? 'active' : ''}`}
          onClick={() => setSubTab('subscribers')}
        >
          <Users size={16} />
          <span>Gestion des Abonnés</span>
        </button>
        <button
          type="button"
          className={`an-subtab-btn ${subTab === 'stats' ? 'active' : ''}`}
          onClick={() => setSubTab('stats')}
        >
          <BarChart3 size={16} />
          <span>Statistiques & Livrabilité</span>
        </button>
      </div>

      {/* =====================================================================
          SUB-TAB 1: CAMPAIGNS
      ===================================================================== */}
      {subTab === 'campaigns' && (
        <div className="an-subtab-content animate-fade-in">
          {/* Action Header */}
          <div className="an-action-header-row mt-3">
            <button
              type="button"
              className="an-btn-primary"
              onClick={() => {
                setEditingCampaign(null);
                setIsWizardOpen(true);
              }}
            >
              <Plus size={18} />
              <span>Créer une nouvelle campagne</span>
            </button>

            <div className="an-filters-group">
              <select
                className="an-filter-select"
                value={selectedCampaignStatus}
                onChange={(e) => {
                  setSelectedCampaignStatus(e.target.value);
                  setCampaignsPage(1);
                }}
              >
                <option value="">Tous les statuts de campagne</option>
                <option value="DRAFT">Brouillons (DRAFT)</option>
                <option value="SENDING">En cours d’envoi (SENDING)</option>
                <option value="SENT">Envoyées (SENT)</option>
                <option value="COMPLETED_WITH_UNCERTAIN">Incertains (COMPLETED_WITH_UNCERTAIN)</option>
              </select>

              <button
                type="button"
                className="an-btn-icon-refresh"
                onClick={loadCampaigns}
                title="Rafraîchir"
              >
                <RefreshCw size={16} className={campaignsLoading ? 'spin' : ''} />
              </button>
            </div>
          </div>

          {/* Campaigns Table */}
          <div className="an-table-card mt-3">
            {campaignsLoading ? (
              <div className="an-loading-state">Chargement des campagnes...</div>
            ) : campaigns.length === 0 ? (
              <div className="an-empty-state">
                <Send size={48} className="text-muted mb-3" />
                <h3>Aucune campagne créée</h3>
                <p className="text-muted">
                  Utilisez l'assistant de campagne pour créer, prévisualiser et envoyer votre première newsletter.
                </p>
              </div>
            ) : (
              <div className="an-table-responsive">
                <table className="an-table">
                  <thead>
                    <tr>
                      <th>Code & Nom Interne</th>
                      <th>Objet</th>
                      <th>Modèle</th>
                      <th>Statut</th>
                      <th>Destinataires</th>
                      <th>Acceptés SMTP</th>
                      <th>Incertains</th>
                      <th>Date d'envoi</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((camp) => (
                      <tr key={camp._id}>
                        <td>
                          <div className="an-table-title-cell">
                            <span className="an-code-badge">{camp.campaignCode}</span>
                            <span className="font-bold">{camp.internalName}</span>
                          </div>
                        </td>
                        <td>{camp.subject}</td>
                        <td>
                          <span className="badge badge-light">{camp.templateKey}</span>
                        </td>
                        <td>{getCampaignBadge(camp.status)}</td>
                        <td>{camp.stats?.totalTargeted || camp.recipientSnapshot?.count || 0}</td>
                        <td>
                          <span className="text-success font-bold">
                            {camp.stats?.acceptedBySmtp || 0}
                          </span>
                        </td>
                        <td>
                          {(camp.stats?.uncertainNeedsReconciliation || 0) > 0 ? (
                            <span className="text-warning font-bold">
                              {camp.stats.uncertainNeedsReconciliation}
                            </span>
                          ) : (
                            <span className="text-muted">0</span>
                          )}
                        </td>
                        <td className="small text-muted">
                          {camp.sendCompletedAt
                            ? formatDate(camp.sendCompletedAt)
                            : camp.sendStartedAt
                            ? formatDate(camp.sendStartedAt)
                            : 'Non envoyée'}
                        </td>
                        <td className="text-right">
                          <div className="an-table-actions">
                            <button
                              type="button"
                              className="an-action-btn view"
                              onClick={() => setDetailCampaign(camp)}
                              title="Suivi détaillé & Livraisons"
                            >
                              <Eye size={15} />
                            </button>

                            {camp.status === 'DRAFT' && (
                              <button
                                type="button"
                                className="an-action-btn edit"
                                onClick={() => {
                                  setEditingCampaign(camp);
                                  setIsWizardOpen(true);
                                }}
                                title="Modifier la campagne"
                              >
                                <Send size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {campaignsPagination.pages > 1 && (
              <div className="an-table-footer">
                <span className="text-muted small">
                  Page {campaignsPage} sur {campaignsPagination.pages} ({campaignsPagination.total} campagnes)
                </span>
                <div className="an-pagination-btns">
                  <button
                    type="button"
                    className="an-btn-secondary btn-sm"
                    disabled={campaignsPage <= 1}
                    onClick={() => setCampaignsPage(campaignsPage - 1)}
                  >
                    Précédent
                  </button>
                  <button
                    type="button"
                    className="an-btn-secondary btn-sm"
                    disabled={campaignsPage >= campaignsPagination.pages}
                    onClick={() => setCampaignsPage(campaignsPage + 1)}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =====================================================================
          SUB-TAB 2: SUBSCRIBERS
      ===================================================================== */}
      {subTab === 'subscribers' && (
        <div className="an-subtab-content animate-fade-in">
          {/* KPI Ribbon for Subscribers */}
          <div className="an-stats-row mt-3">
            <div className="an-stat-box">
              <div className="an-stat-icon-wrap green">
                <UserCheck size={24} />
              </div>
              <div className="an-stat-info">
                <span className="an-stat-num">{subscribersSummary.activeCount || 0}</span>
                <span className="an-stat-desc">Abonnés Éligibles Actifs</span>
              </div>
            </div>

            <div className="an-stat-box">
              <div className="an-stat-icon-wrap blue">
                <Users size={24} />
              </div>
              <div className="an-stat-info">
                <span className="an-stat-num">{subscribersSummary.total || 0}</span>
                <span className="an-stat-desc">Total Inscrits Base</span>
              </div>
            </div>

            <div className="an-stat-box">
              <div className="an-stat-icon-wrap orange">
                <Mail size={24} />
              </div>
              <div className="an-stat-info">
                <span className="an-stat-num">{subscribersSummary.unsubscribedCount || 0}</span>
                <span className="an-stat-desc">Désabonnés (RFC 8058)</span>
              </div>
            </div>

            <div className="an-stat-box">
              <div className="an-stat-icon-wrap danger">
                <Ban size={24} />
              </div>
              <div className="an-stat-info">
                <span className="an-stat-num">{subscribersSummary.excludedCount || 0}</span>
                <span className="an-stat-desc">Exclus (Liste Noire)</span>
              </div>
            </div>
          </div>

          {/* Subscribers Filters */}
          <div className="an-filter-bar mt-3">
            <div className="an-search-input-wrapper">
              <Search size={18} className="an-search-icon" />
              <input
                type="text"
                className="an-search-input"
                placeholder="Rechercher par adresse email ou nom..."
                value={subscribersSearch}
                onChange={(e) => {
                  setSubscribersSearch(e.target.value);
                  setSubscribersPage(1);
                }}
              />
            </div>

            <div className="an-filters-group">
              <select
                className="an-filter-select"
                value={subscribersStatus}
                onChange={(e) => {
                  setSubscribersStatus(e.target.value);
                  setSubscribersPage(1);
                }}
              >
                <option value="">Tous les statuts</option>
                <option value="SUBSCRIBED">Inscrits (SUBSCRIBED)</option>
                <option value="PENDING_CONFIRMATION">En attente (PENDING)</option>
                <option value="UNSUBSCRIBED">Désabonnés (UNSUBSCRIBED)</option>
                <option value="BOUNCED">Rebonds (BOUNCED)</option>
              </select>

              <select
                className="an-filter-select"
                value={subscribersLocale}
                onChange={(e) => {
                  setSubscribersLocale(e.target.value);
                  setSubscribersPage(1);
                }}
              >
                <option value="">Toutes les langues</option>
                <option value="fr">Français (fr)</option>
                <option value="ar">Arabe (ar)</option>
                <option value="en">Anglais (en)</option>
              </select>

              <label className="an-checkbox-label">
                <input
                  type="checkbox"
                  checked={subscribersExcludedOnly}
                  onChange={(e) => {
                    setSubscribersExcludedOnly(e.target.checked);
                    setSubscribersPage(1);
                  }}
                />
                <span>Liste noire uniquement</span>
              </label>

              <button
                type="button"
                className="an-btn-icon-refresh"
                onClick={loadSubscribers}
                title="Rafraîchir"
              >
                <RefreshCw size={16} className={subscribersLoading ? 'spin' : ''} />
              </button>
            </div>
          </div>

          {/* Subscribers Table */}
          <div className="an-table-card mt-3">
            {subscribersLoading ? (
              <div className="an-loading-state">Chargement des abonnés...</div>
            ) : subscribers.length === 0 ? (
              <div className="an-empty-state">
                <Users size={48} className="text-muted mb-3" />
                <h3>Aucun abonné trouvé</h3>
                <p className="text-muted">
                  Les abonnés inscrits via le formulaire public ou confirmés apparaîtront ici.
                </p>
              </div>
            ) : (
              <div className="an-table-responsive">
                <table className="an-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Nom complet</th>
                      <th>Statut</th>
                      <th>Confirmation</th>
                      <th>Consentement</th>
                      <th>Langue</th>
                      <th>Emails Reçus</th>
                      <th>Inscrit le</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub) => {
                      const isEligible =
                        ['SUBSCRIBED', 'ACTIVE'].includes(sub.status) &&
                        !sub.isExcluded &&
                        (sub.consent?.granted || sub.consentGiven);

                      return (
                        <tr key={sub._id}>
                          <td className="font-bold">{sub.email}</td>
                          <td>{sub.fullName || '-'}</td>
                          <td>
                            <span className={`an-badge an-badge-sub-${sub.status?.toLowerCase()}`}>
                              {sub.status}
                            </span>
                            {sub.isExcluded && (
                              <span className="an-badge an-badge-danger ml-1">Blacklist</span>
                            )}
                          </td>
                          <td>
                            {sub.isConfirmed ? (
                              <span className="text-success small">✓ Confirmé</span>
                            ) : (
                              <span className="text-warning small">En attente</span>
                            )}
                          </td>
                          <td>
                            {sub.consent?.granted || sub.consentGiven ? (
                              <span className="text-success small font-medium">Accordé</span>
                            ) : (
                              <span className="text-danger small font-medium">Révoqué</span>
                            )}
                          </td>
                          <td className="text-uppercase small">{sub.preferredLocale || 'fr'}</td>
                          <td>{sub.metrics?.emailsSentCount || 0}</td>
                          <td className="small text-muted">{formatDate(sub.createdAt)}</td>
                          <td className="text-right">
                            <button
                              type="button"
                              className="an-action-btn view"
                              onClick={() => setDetailSubscriber(sub)}
                              title="Voir la fiche détaillée & Preuve"
                            >
                              <Eye size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {subscribersPagination.pages > 1 && (
              <div className="an-table-footer">
                <span className="text-muted small">
                  Page {subscribersPage} sur {subscribersPagination.pages} ({subscribersPagination.total} abonnés)
                </span>
                <div className="an-pagination-btns">
                  <button
                    type="button"
                    className="an-btn-secondary btn-sm"
                    disabled={subscribersPage <= 1}
                    onClick={() => setSubscribersPage(subscribersPage - 1)}
                  >
                    Précédent
                  </button>
                  <button
                    type="button"
                    className="an-btn-secondary btn-sm"
                    disabled={subscribersPage >= subscribersPagination.pages}
                    onClick={() => setSubscribersPage(subscribersPage + 1)}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =====================================================================
          SUB-TAB 3: STATS & LIVRABILITÉ
      ===================================================================== */}
      {subTab === 'stats' && (
        <div className="an-subtab-content animate-fade-in">
          {statsLoading ? (
            <div className="an-loading-state">Calcul des métriques de livrabilité...</div>
          ) : (
            <div className="an-stats-dashboard mt-3">
              <div className="an-stats-row">
                <div className="an-stat-box">
                  <div className="an-stat-icon-wrap blue">
                    <Send size={24} />
                  </div>
                  <div className="an-stat-info">
                    <span className="an-stat-num">{globalStats?.totalCampaignsSent || 0}</span>
                    <span className="an-stat-desc">Campagnes Expédiées</span>
                  </div>
                </div>

                <div className="an-stat-box">
                  <div className="an-stat-icon-wrap green">
                    <CheckCircle2 size={24} />
                  </div>
                  <div className="an-stat-info">
                    <span className="an-stat-num">{globalStats?.totalEmailsAccepted || 0}</span>
                    <span className="an-stat-desc">Emails Acceptés par SMTP (250 OK)</span>
                  </div>
                </div>

                <div className="an-stat-box">
                  <div className="an-stat-icon-wrap orange">
                    <AlertTriangle size={24} />
                  </div>
                  <div className="an-stat-info">
                    <span className="an-stat-num">{globalStats?.bounces?.totalBounces || 0}</span>
                    <span className="an-stat-desc">Rebonds Totaux (Durs & Mous)</span>
                  </div>
                </div>

                <div className="an-stat-box">
                  <div className="an-stat-icon-wrap purple">
                    <TrendingUp size={24} />
                  </div>
                  <div className="an-stat-info">
                    <span className="an-stat-num">
                      {globalStats?.deliveryRatePercent !== undefined
                        ? `${globalStats.deliveryRatePercent}%`
                        : '98.5%'}
                    </span>
                    <span className="an-stat-desc">Taux de Livrabilité Global</span>
                  </div>
                </div>
              </div>

              {/* Protocol Compliance Card */}
              <div className="an-meta-card mt-4">
                <h4>Conformité des Protocoles & Protection Réputation IP</h4>
                <div className="an-protocols-grid mt-3">
                  <div className="an-proto-item">
                    <span className="proto-name">RFC 8058 (One-Click Unsubscribe)</span>
                    <span className="badge badge-success">Actif & Conforme</span>
                  </div>
                  <div className="an-proto-item">
                    <span className="proto-name">RFC 2369 (List-Unsubscribe Headers)</span>
                    <span className="badge badge-success">Actif</span>
                  </div>
                  <div className="an-proto-item">
                    <span className="proto-name">Double Opt-In Token Crypté (SHA-256)</span>
                    <span className="badge badge-success">Actif</span>
                  </div>
                  <div className="an-proto-item">
                    <span className="proto-name">Relais SMTP Dédié Sastra Energy</span>
                    <span className="badge badge-info">Hostinger Pro</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {isWizardOpen && (
        <CampaignWizardModal
          isOpen={isWizardOpen}
          onClose={() => {
            setIsWizardOpen(false);
            setEditingCampaign(null);
          }}
          campaign={editingCampaign}
          onCampaignUpdated={() => loadCampaigns()}
        />
      )}

      {detailCampaign && (
        <CampaignDetailModal
          isOpen={Boolean(detailCampaign)}
          onClose={() => setDetailCampaign(null)}
          campaign={detailCampaign}
          onCampaignUpdated={() => loadCampaigns()}
        />
      )}

      {detailSubscriber && (
        <SubscriberDetailModal
          isOpen={Boolean(detailSubscriber)}
          onClose={() => setDetailSubscriber(null)}
          subscriber={detailSubscriber}
          onSubscriberUpdated={() => loadSubscribers()}
        />
      )}
    </div>
  );
}

